import { KidTemplateAffectedShareClass } from '../../../../api/kids/types';
import type { ShareClassModel } from '../../../../api/shareclass/types';
import type { FundOption } from './templateManagement.types';

export type KidTemplateDocumentCoverageState =
  | 'present'
  | 'missing'
  | 'invalid';

export type KidTemplateDocumentRow = KidTemplateAffectedShareClass & {
  coverageState: KidTemplateDocumentCoverageState;
};

export const EMPTY_KID_TEMPLATE_AFFECTED_ROWS: KidTemplateAffectedShareClass[] =
  [];

export const EMPTY_KID_TEMPLATE_DOCUMENT_ROWS: KidTemplateDocumentRow[] = [];

export type KidTemplateShareClassDisplaySource = Pick<
  KidTemplateAffectedShareClass,
  | 'share_class_id'
  | 'share_class_isin'
  | 'share_class_name_full'
  | 'share_class_name_long'
>;

export type KidTemplateFundDisplaySource = Pick<
  KidTemplateAffectedShareClass,
  'fund_id' | 'fund_name' | 'fund_name_full' | 'fund_name_long'
>;

/**
 * Long/full share class name for UI — never `share_class_name`, never ISIN.
 * Keys match overview / share-class API (`_full`) and template payloads (`_long`).
 */
export const formatKidTemplateShareClassName = (
  row: KidTemplateShareClassDisplaySource,
): string =>
  row.share_class_name_full?.trim() ||
  row.share_class_name_long?.trim() ||
  row.share_class_id;

/** Short fund name shown as Fund ID (overview `fund_name`). */
export const formatKidTemplateFundId = (
  row: KidTemplateFundDisplaySource,
): string => row.fund_name?.trim() || row.fund_id;

/** Full fund name shown as Fund Name (overview `fund_name_full`). */
export const formatKidTemplateFundName = (
  row: KidTemplateFundDisplaySource,
): string =>
  row.fund_name_full?.trim() ||
  row.fund_name_long?.trim() ||
  formatKidTemplateFundId(row);

/** Name · ISIN (overview-style; name first). */
export const formatKidTemplateShareClassLabel = (
  row: KidTemplateShareClassDisplaySource,
): string => {
  const name = formatKidTemplateShareClassName(row);
  const isin = row.share_class_isin?.trim();
  if (name && isin) return `${name} · ${isin}`;
  return name || isin || row.share_class_id;
};

/** Fill `_full` / ISIN from the share-class API (same source as Fund Overview). */
export const enrichKidTemplateDocumentRowsWithShareClasses = (
  rows: KidTemplateDocumentRow[],
  shareClasses: ShareClassModel[] | undefined,
): KidTemplateDocumentRow[] => {
  if (!shareClasses?.length) return rows;

  const byFundAndId = new Map<string, ShareClassModel>();
  shareClasses.forEach((shareClass) => {
    byFundAndId.set(
      `${shareClass.fund_id}:${shareClass.share_class_id}`,
      shareClass,
    );
  });

  return rows.map((row) => {
    const shareClass = byFundAndId.get(`${row.fund_id}:${row.share_class_id}`);
    if (!shareClass) return row;

    return {
      ...row,
      share_class_name_full:
        row.share_class_name_full?.trim() ||
        row.share_class_name_long?.trim() ||
        shareClass.share_class_name_full ||
        undefined,
      share_class_isin: row.share_class_isin || shareClass.share_class_isin,
    };
  });
};

/** Fill `fund_name` / `fund_name_full` from the funds API (same source as Fund Overview). */
export const enrichKidTemplateDocumentRowsWithFunds = (
  rows: KidTemplateDocumentRow[],
  funds: FundOption[] | undefined,
): KidTemplateDocumentRow[] => {
  if (!funds?.length) return rows;

  const byId = new Map(funds.map((fund) => [fund.fund_id, fund]));

  return rows.map((row) => {
    const fund = byId.get(row.fund_id);
    if (!fund) return row;

    return {
      ...row,
      fund_name: row.fund_name?.trim() || fund.fund_name || undefined,
      fund_name_full:
        row.fund_name_full?.trim() ||
        fund.fund_name_full ||
        row.fund_name_long ||
        undefined,
    };
  });
};

export const kidTemplateDocumentRowKey = (
  row: Pick<
    KidTemplateAffectedShareClass,
    'fund_id' | 'share_class_id' | 'document_language'
  >,
): string => `${row.fund_id}-${row.share_class_id}-${row.document_language}`;

export const kidTemplateDocumentCoverageSortRank: Record<
  KidTemplateDocumentCoverageState,
  number
> = {
  present: 0,
  missing: 1,
  invalid: 2,
};

export const mergeKidTemplateDocumentRows = (
  affected: KidTemplateAffectedShareClass[],
  missing: KidTemplateAffectedShareClass[],
  invalid: KidTemplateAffectedShareClass[],
): KidTemplateDocumentRow[] => {
  const missingKeys = new Set(missing.map(kidTemplateDocumentRowKey));
  const invalidKeys = new Set(invalid.map(kidTemplateDocumentRowKey));
  const byKey = new Map<string, KidTemplateDocumentRow>();

  const upsert = (
    row: KidTemplateAffectedShareClass,
    coverageState: KidTemplateDocumentCoverageState,
  ) => {
    const key = kidTemplateDocumentRowKey(row);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...row, coverageState });
      return;
    }

    const useIncomingCoverage =
      kidTemplateDocumentCoverageSortRank[coverageState] >
      kidTemplateDocumentCoverageSortRank[existing.coverageState];
    const primary = useIncomingCoverage ? row : existing;
    const secondary = useIncomingCoverage ? existing : row;

    byKey.set(key, {
      ...secondary,
      ...primary,
      share_class_isin:
        row.share_class_isin || existing.share_class_isin || undefined,
      fund_name_long: row.fund_name_long || existing.fund_name_long,
      fund_name: row.fund_name || existing.fund_name,
      fund_name_full: row.fund_name_full || existing.fund_name_full,
      share_class_name: row.share_class_name || existing.share_class_name,
      share_class_name_full:
        row.share_class_name_full || existing.share_class_name_full || undefined,
      share_class_name_long:
        row.share_class_name_long || existing.share_class_name_long || undefined,
      coverageState: useIncomingCoverage
        ? coverageState
        : existing.coverageState,
    });
  };

  affected.forEach((row) => {
    const key = kidTemplateDocumentRowKey(row);
    if (invalidKeys.has(key)) {
      upsert(row, 'invalid');
    } else if (missingKeys.has(key)) {
      upsert(row, 'missing');
    } else {
      upsert(row, 'present');
    }
  });

  missing.forEach((row) => upsert(row, 'missing'));
  invalid.forEach((row) => upsert(row, 'invalid'));

  return [...byKey.values()].sort((a, b) =>
    formatKidTemplateShareClassName(a).localeCompare(
      formatKidTemplateShareClassName(b),
      undefined,
      { sensitivity: 'base' },
    ),
  );
};

export const kidTemplateDocumentCoverageLabel: Record<
  KidTemplateDocumentCoverageState,
  string
> = {
  present: 'Template is on this document',
  missing: 'Add this template to the document',
  invalid: 'Remove this template from the document',
};

export const kidTemplateDocumentCoverageChipLabel: Record<
  KidTemplateDocumentCoverageState,
  string
> = {
  present: 'Complete',
  missing: 'Missing',
  invalid: 'Invalid',
};

export const kidTemplateDocumentCoverageBadgeClass: Record<
  KidTemplateDocumentCoverageState,
  string
> = {
  present: 'bg-emerald-100 text-emerald-950 ring-emerald-200',
  missing: 'bg-amber-100 text-amber-950 ring-amber-200',
  invalid: 'bg-rose-100 text-rose-950 ring-rose-200',
};
